"use client"

import { useEffect } from "react"

/**
 * useSeo — per-route <head> metadata for the client-rendered site.
 *
 * The app is a single HTML shell (public/index.html) reused for every route,
 * so without this every page shares one <title>/description. This hook
 * imperatively syncs the document head to the current route: title, meta
 * description, canonical, Open Graph and Twitter tags, and (optionally) a
 * JSON-LD block. It creates tags that the shell doesn't already have and
 * updates the ones it does.
 *
 * Crawlers that don't run JS never see these updates (they get the static
 * shell + the #pre-react-fallback content); this is for Googlebot, Bing and
 * social/link scrapers, which do render.
 */

const SITE_ORIGIN = "https://techtok.me"
const DEFAULT_IMAGE = `${SITE_ORIGIN}/images/og-card.jpg`
const JSONLD_ID = "route-jsonld"

type UseSeoInput = {
  /** Full <title> text. */
  title: string
  /** Meta description (~150-160 chars ideal). */
  description?: string
  /** Route path starting with "/", e.g. "/missies". Used for canonical + og:url. */
  path: string
  /** Absolute URL or site-root-relative path to the share image. */
  image?: string
  /** og:type. "website" for pages, "article" for /ontdek posts. */
  type?: "website" | "article"
  /** ISO date — emits article:published_time (only when type is "article"). */
  publishedTime?: string
  /** Byline — emits article:author (only when type is "article"). */
  author?: string
  /** When true, emit robots "noindex, follow" (e.g. the 404 page). */
  noindex?: boolean
  /** Optional schema.org object injected as <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown>
}

function upsertMeta(key: string, attr: "name" | "property", content: string) {
  const selector = `meta[${attr}="${key}"]`
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement("meta")
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute("content", content)
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement("link")
    el.setAttribute("rel", "canonical")
    document.head.appendChild(el)
  }
  el.setAttribute("href", href)
}

export function useSeo({
  title,
  description,
  path,
  image,
  type = "website",
  publishedTime,
  author,
  noindex = false,
  jsonLd,
}: UseSeoInput) {
  const resolvedImage = !image ? DEFAULT_IMAGE : image.startsWith("http") ? image : `${SITE_ORIGIN}${image}`
  const url = `${SITE_ORIGIN}${path}`
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : null

  useEffect(() => {
    document.title = title

    upsertMeta("robots", "name", noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1")
    upsertCanonical(url)

    if (description) {
      upsertMeta("description", "name", description)
      upsertMeta("og:description", "property", description)
      upsertMeta("twitter:description", "name", description)
    }

    upsertMeta("og:title", "property", title)
    upsertMeta("twitter:title", "name", title)
    upsertMeta("og:url", "property", url)
    upsertMeta("og:type", "property", type)
    upsertMeta("og:image", "property", resolvedImage)
    upsertMeta("twitter:image", "name", resolvedImage)
    upsertMeta("twitter:card", "name", "summary_large_image")

    // article:* tags only make sense on article routes — always clear any that
    // a previous route left behind, then re-add for the current article.
    document.head.querySelectorAll('meta[property^="article:"]').forEach((el) => el.remove())
    if (type === "article") {
      if (publishedTime) upsertMeta("article:published_time", "property", publishedTime)
      if (author) upsertMeta("article:author", "property", author)
    }

    let script: HTMLScriptElement | null = null
    if (jsonLdString) {
      script = document.getElementById(JSONLD_ID) as HTMLScriptElement | null
      if (!script) {
        script = document.createElement("script")
        script.type = "application/ld+json"
        script.id = JSONLD_ID
        document.head.appendChild(script)
      }
      script.textContent = jsonLdString
    }

    return () => {
      // The route-specific JSON-LD and article:* tags are torn down between
      // routes; the shell's own tags stay and are overwritten by the next
      // page's useSeo.
      if (script && script.parentNode) script.parentNode.removeChild(script)
      document.head.querySelectorAll('meta[property^="article:"]').forEach((el) => el.remove())
    }
  }, [title, description, url, resolvedImage, type, publishedTime, author, noindex, jsonLdString])
}
